import { createSelector } from '@reduxjs/toolkit';

import { ReduxState } from '../types';
import { AdvancedSearchState } from '../types/advanced-search';
import { QuickSearchState } from '../types/quick-search';
import { SavedFundsState } from '../types/saved-funds';

import { convertShelvesToShelfStatuses } from '../../../utils/shelf-management';
import Portfolio from '@/types/portfolio';
import Comparison from '@/types/comparison';

// Base slice selectors
const selectComparison = (state: ReduxState) => state.comparison;
const selectPortfolio = (state: Portfolio) => state;
const selectQuickSearch = (state: QuickSearchState) => state;
const selectAdvancedSearch = (state: AdvancedSearchState) => state;
const selectSavedFunds = (state: SavedFundsState) => state;

// Memoized selectors
// Active Comparison items holdings
const selectActiveComparison = createSelector([selectComparison], comparison => comparison.activeComparison);

const selectActiveComparisonItems = createSelector([selectActiveComparison], activeComparison => {
  if (activeComparison) {
    return activeComparison.items;
  }

  return [];
});

export const selectShelfStatusedComparison = createSelector(
  [selectComparison, selectActiveComparison, selectActiveComparisonItems],
  (comparison, activeComparison, activeComparisonItems) => {
    const items = activeComparisonItems.map(item => ({
      ...item,
      holdings: item.holdings.map(holding => {
        const shelfStatuses = convertShelvesToShelfStatuses(holding.filteredShelves, holding.complianceComparisons);
        return {
          ...holding,
          flags: shelfStatuses.length > 0 ? [...(holding.flags || []), ...shelfStatuses] : holding.flags || [],
        };
      }),
    }));

    let active: Comparison[] = [];
    if (activeComparison) {
      active = [
        {
          ...activeComparison,
          items,
        },
      ];
    }

    return {
      ...comparison,
      activeComparisons: active,
    };
  },
);

// Portfolio Holdings (Edit portfolio)
const selectEditPortfolioHoldings = createSelector([selectPortfolio], editPortfolioPortfolio => editPortfolioPortfolio.holdings);

export const selectShelfStatusedPortfolioHoldings = createSelector([selectPortfolio, selectEditPortfolioHoldings], (portfolio, holdings) => {
  const portfolioHoldings = holdings.map(holding => {
    const shelfStatuses = convertShelvesToShelfStatuses(holding.filteredShelves, holding.complianceComparisons);
    return {
      ...holding,
      flags: shelfStatuses.length > 0 ? [...(holding.flags || []), ...shelfStatuses] : holding.flags || [],
    };
  });

  return {
    ...portfolio,
    holdings: portfolioHoldings,
  };
});

// Quick Search
const selectQuickSearchResults = createSelector([selectQuickSearch], quickSearch => quickSearch.results);

export const selectQuickSearchShelfStatusedResults = createSelector([selectQuickSearch, selectQuickSearchResults], (quickSearch, quickSearchResults) => {
  const results = quickSearchResults.map(result => {
    const shelfStatuses = convertShelvesToShelfStatuses(result.filteredShelves, result.complianceComparisons);
    return {
      ...result,
      flags: shelfStatuses.length > 0 ? [...(result.flags || []), ...shelfStatuses] : result.flags || [],
    };
  });

  return {
    ...quickSearch,
    results,
  };
});

const selectRecentSearch = createSelector([selectQuickSearch], quickSearch => quickSearch.recentSearchItems);

export const selectRecentSearchResults = createSelector([selectQuickSearch, selectRecentSearch], (quickSearch, recentSearchItems) => ({
  ...quickSearch,
  recentSearchItems,
}));

// Advanced Fund Search
const selectAdvancedSearchFund = createSelector([selectAdvancedSearch], advancedSearch => advancedSearch.fund);

const selectAdvancedSearchFunds = createSelector([selectAdvancedSearchFund], advancedSearchFund => advancedSearchFund.funds);

export const selectAdvancedSearchShelfStatusedFund = createSelector(
  [selectAdvancedSearchFund, selectAdvancedSearchFunds],
  (advancedSearchFund, advancedSearchFunds) => {
    if (!advancedSearchFunds) {
      return advancedSearchFund;
    }

    const shelfStatusedResults = advancedSearchFunds.map(fund => {
      const shelfStatuses = convertShelvesToShelfStatuses(fund.filteredShelves, fund.complianceComparisons);
      return {
        ...fund,
        flags: shelfStatuses.length > 0 ? [...(fund.flags || []), ...shelfStatuses] : fund.flags || [],
      };
    });

    return {
      ...advancedSearchFund,
      funds: shelfStatusedResults,
    };
  },
);

// Saved Fund Search
const selectSavedFund = createSelector([selectSavedFunds], savedFundsState => savedFundsState.funds);

export const selectSavedFundsShelfStatusedFund = createSelector([selectSavedFunds, selectSavedFund], (savedFundsState, savedFunds) => {
  const shelfStatusedResults = savedFunds.map(fund => {
    const shelfStatuses = convertShelvesToShelfStatuses(fund.filteredShelves, fund.complianceComparisons);
    return {
      ...fund,
      flags: shelfStatuses.length > 0 ? [...(fund.flags || []), ...shelfStatuses] : fund.flags || [],
    };
  });

  return {
    ...savedFundsState,
    funds: shelfStatusedResults,
  };
});
